import React, { useEffect, useState } from "react";
import RecordPage from "../../components/RecordPage";
import Modal from "../../components/Modal";
import api, { errMsg } from "../../core/api";
import { useApp } from "../../core/AppContext";
import { faMoney, formatDate, todayIso } from "../../core/format";

import { expensePrint } from "../../core/printConfigs";

const STATUS = [
  { value: "pending", label: "در انتظار تأیید" },
  { value: "approved", label: "تأیید شد" },
  { value: "rejected", label: "رد شد" },
];
const STATUS_LABEL = { pending: "در انتظار تأیید", approved: "تأیید شد", rejected: "رد شد" };
const TONE = { pending: "warn", approved: "ok", rejected: "bad" };

function RejectModal({ onClose, onSubmit }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (text.trim().length < 3) return setErr("دلیل رد را بنویسید");
    setBusy(true);
    setErr("");
    try {
      await onSubmit(text.trim());
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <Modal small title="رد هزینه ثبت‌شده" onClose={() => !busy && onClose()}
      footer={<>
        <button className="btn btn-danger" onClick={go} disabled={busy}>{busy ? "..." : "رد"}</button>
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>انصراف</button>
      </>}>
      {err && <div className="alert err">{err}</div>}
      <div className="field">
        <label>دلیل رد (برای ثبت‌کننده نمایش داده می‌شود)</label>
        <textarea rows={4} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
      </div>
    </Modal>
  );
}

export default function Expenses() {
  const { meta, user, toast, confirm } = useApp();
  const admin = user.role === "admin";
  const staff = user.role === "staff";

  const cats = (meta?.expenseCategories || []).map((c) => ({ value: c, label: c }));
  const [total, setTotal] = useState(null);
  const [tick, setTick] = useState(0);
  const [rejectFor, setRejectFor] = useState(null);
  useEffect(() => { api.get("/reports/expenses").then((r) => setTotal(r.data.total)).catch(() => {}); }, [tick]);

  const approve = async (r, load) => {
    if (!(await confirm("این هزینه تأیید و ثبت نهایی شود؟", { okText: "تأیید", danger: false }))) return;
    try {
      await api.post(`/expenses/${r.id}/decision`, { approve: true });
      toast("تأیید شد");
      load();
      setTick((t) => t + 1);
    } catch (e) { toast(errMsg(e), "err"); }
  };

  const reject = async (note) => {
    await api.post(`/expenses/${rejectFor.row.id}/decision`, { approve: false, note });
    toast("رد شد");
    rejectFor.load();
    setRejectFor(null);
    setTick((t) => t + 1);
  };

  const subtitle = staff
    ? "هزینه‌های ثبت‌شده‌ی شما پس از تأیید مسئول ایمنی نهایی می‌شود"
    : total !== null
      ? `جمع کل هزینه‌های تأییدشده: ${faMoney(total)} ریال`
      : "ثبت هزینه‌های انجام‌شده";

  return (
    <>
      <RecordPage printReport={expensePrint} title="هزینه‌ها" subtitle={subtitle}
        endpoint="/expenses" multipart createLabel="ثبت هزینه"
        canCreate={admin || staff} canEdit={admin} canDelete={admin}
        initial={{ expenseDate: todayIso() }}
        filters={[
          { name: "category", label: "دسته‌بندی", options: cats },
          ...(admin || staff ? [{ name: "status", label: "وضعیت", options: STATUS }] : []),
        ]}
        columns={[
          { key: "expenseDate", label: "تاریخ", render: (r) => formatDate(r.expenseDate) },
          { key: "title", label: "شرح هزینه", render: (r) => <b>{r.title}</b> },
          { key: "category", label: "دسته‌بندی" },
          { key: "payee", label: "طرف حساب" },
          { key: "refNo", label: "شماره سند", render: (r) => <span dir="ltr">{r.refNo || "—"}</span> },
          { key: "amount", label: "مبلغ (ریال)", num: true, render: (r) => <b>{faMoney(r.amount)}</b> },
          ...(admin ? [{ key: "creatorName", label: "ثبت‌کننده" }] : []),
          {
            key: "status", label: "وضعیت",
            render: (r) => (
              <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
                <span className={`badge ${TONE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                {r.decisionNote && <small className="hint">{r.decisionNote}</small>}
              </div>
            ),
          },
        ]}
        fields={[
          { name: "expenseDate", label: "تاریخ", type: "date", required: true, full: true },
          { name: "title", label: "شرح هزینه", required: true, full: true },
          { name: "category", label: "دسته‌بندی", type: "select", options: cats, required: true },
          { name: "amount", label: "مبلغ (ریال)", type: "money", required: true },
          { name: "payee", label: "طرف حساب / دریافت‌کننده" },
          { name: "refNo", label: "شماره فاکتور / سند", ltr: true },
          { name: "description", label: "توضیحات", full: true },
          { name: "file", label: "تصویر فاکتور (اختیاری)", type: "file", full: true },
        ]}
        rowExtra={(r, load) =>
          admin && r.status === "pending" ? (
            <>
              <button className="btn btn-sm" onClick={() => approve(r, load)}><i className="fa-solid fa-check" /> تأیید</button>
              <button className="btn btn-sm btn-danger" onClick={() => setRejectFor({ row: r, load })}><i className="fa-solid fa-xmark" /> رد</button>
            </>
          ) : null
        } />
      {rejectFor && <RejectModal onClose={() => setRejectFor(null)} onSubmit={reject} />}
    </>
  );
}
